import React, { useState } from 'react';
import { Card, Table, Tag, Empty, Tabs, Avatar } from 'antd';
import { 
  TrophyOutlined, 
  RiseOutlined, 
  FallOutlined,
  UserOutlined,
  FireOutlined,
  FrownOutlined
} from '@ant-design/icons';
import './OpponentStats.css';

const { TabPane } = Tabs;

/**
 * 双打对手统计组件
 * 展示对手统计列表和排行榜
 */
const OpponentStats = ({ opponents, loading }) => {
  const [activeRanking, setActiveRanking] = useState('mostMatches');

  if (loading) {
    return (
      <div className="opponent-stats loading">
        <div className="loading-text">加载中...</div>
      </div>
    );
  }

  if (!opponents || !opponents.all || opponents.all.length === 0) {
    return (
      <div className="opponent-stats empty">
        <Empty description="暂无对手统计数据" />
      </div>
    );
  }

  const { all: opponentList, rankings } = opponents;

  // 表格列定义
  const columns = [
    {
      title: '对手',
      dataIndex: 'opponentName',
      key: 'opponentName',
      render: (text, record) => (
        <div className="opponent-info">
          <Avatar 
            src={record.opponentAvatar} 
            icon={<UserOutlined />}
            size="small"
            className="opponent-avatar"
          />
          <span className="opponent-name">{text}</span>
        </div>
      ),
      width: 120
    },
    {
      title: '交手次数',
      dataIndex: 'matchesPlayed',
      key: 'matchesPlayed',
      align: 'center',
      sorter: (a, b) => a.matchesPlayed - b.matchesPlayed,
      width: 100
    },
    {
      title: '胜场',
      dataIndex: 'winsAgainst',
      key: 'winsAgainst',
      align: 'center',
      render: (value) => <span style={{ color: '#52c41a' }}>{value}</span>,
      width: 80
    },
    {
      title: '负场',
      dataIndex: 'lossesAgainst',
      key: 'lossesAgainst',
      align: 'center',
      render: (value) => <span style={{ color: '#ff4d4f' }}>{value}</span>,
      width: 80
    },
    {
      title: '胜率',
      dataIndex: 'winRateAgainst',
      key: 'winRateAgainst',
      align: 'center',
      render: (value) => (
        <Tag color={value >= 50 ? 'success' : 'default'}>
          {value}%
        </Tag>
      ),
      sorter: (a, b) => a.winRateAgainst - b.winRateAgainst,
      width: 100
    },
    {
      title: '净得分',
      dataIndex: 'netPoints',
      key: 'netPoints',
      align: 'center',
      render: (value) => (
        <span style={{ color: value >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {value >= 0 ? '+' : ''}{value}
        </span>
      ),
      sorter: (a, b) => a.netPoints - b.netPoints,
      width: 100
    },
    {
      title: '平均净得分',
      dataIndex: 'avgNetPoints',
      key: 'avgNetPoints',
      align: 'center',
      render: (value) => (
        <span style={{ color: value >= 0 ? '#52c41a' : '#ff4d4f' }}>
          {value >= 0 ? '+' : ''}{value.toFixed(1)}
        </span>
      ),
      sorter: (a, b) => a.avgNetPoints - b.avgNetPoints,
      width: 120
    },
    {
      title: '赢分',
      dataIndex: 'ratingGained',
      key: 'ratingGained',
      align: 'center',
      render: (value) => (
        <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
          +{value || 0}
        </span>
      ),
      sorter: (a, b) => (a.ratingGained || 0) - (b.ratingGained || 0),
      width: 100
    },
    {
      title: '输分',
      dataIndex: 'ratingLost',
      key: 'ratingLost',
      align: 'center',
      render: (value) => (
        <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
          -{value || 0}
        </span>
      ),
      sorter: (a, b) => (a.ratingLost || 0) - (b.ratingLost || 0),
      width: 100
    },
    {
      title: '净积分',
      dataIndex: 'netRatingChange',
      key: 'netRatingChange',
      align: 'center',
      render: (value) => {
        const netValue = value || 0;
        return (
          <span style={{ 
            color: netValue >= 0 ? '#52c41a' : '#ff4d4f',
            fontWeight: 'bold'
          }}>
            {netValue >= 0 ? '+' : ''}{netValue}
          </span>
        );
      },
      sorter: (a, b) => (a.netRatingChange || 0) - (b.netRatingChange || 0),
      width: 100
    },
    {
      title: '最近交手',
      dataIndex: 'recentForm',
      key: 'recentForm',
      align: 'center',
      render: (value) => {
        if (!value || value.length === 0) return '-';
        return (
          <div className="recent-form">
            {value.split('').map((result, index) => (
              <Tag 
                key={index}
                color={result === 'W' ? 'success' : 'error'}
                style={{ margin: '2px' }}
              >
                {result}
              </Tag>
            ))}
          </div>
        );
      },
      width: 120
    }
  ];

  // 排行榜卡片组件
  const RankingCard = ({ title, icon, data, sortKey, formatValue }) => {
    if (!data || data.length === 0) {
      return (
        <Card className="ranking-card">
          <Empty description="暂无数据" size="small" />
        </Card>
      );
    }

    return (
      <Card className="ranking-card" title={title}>
        <div className="ranking-list">
          {data.map((opponent, index) => (
            <div key={opponent.opponentId} className="ranking-item">
              <div className="ranking-rank">
                <span className={`rank-badge rank-${index + 1}`}>
                  {index + 1}
                </span>
              </div>
              <Avatar 
                src={opponent.opponentAvatar} 
                icon={<UserOutlined />}
                size="small"
                className="ranking-avatar"
              />
              <div className="ranking-info">
                <div className="ranking-name">{opponent.opponentName}</div>
                <div className="ranking-value">
                  {formatValue ? formatValue(opponent[sortKey]) : opponent[sortKey]}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  // 排行榜Tab配置
  const rankingTabs = [
    {
      key: 'mostMatches',
      label: '交手最多',
      icon: <TrophyOutlined />,
      data: rankings.mostMatches || [],
      sortKey: 'matchesPlayed',
      formatValue: (value) => `${value}次`
    },
    {
      key: 'mostWins',
      label: '战胜最多',
      icon: <FireOutlined />,
      data: rankings.mostWins || [],
      sortKey: 'winsAgainst',
      formatValue: (value) => `${value}次`
    },
    {
      key: 'mostLosses',
      label: '失败最多',
      icon: <FrownOutlined />,
      data: rankings.mostLosses || [],
      sortKey: 'lossesAgainst',
      formatValue: (value) => `${value}次`
    },
    {
      key: 'highestWinRate',
      label: '胜率最高',
      icon: <RiseOutlined />,
      data: rankings.highestWinRate || [],
      sortKey: 'winRateAgainst',
      formatValue: (value) => `${value}%`
    },
    {
      key: 'lowestWinRate',
      label: '胜率最低',
      icon: <FallOutlined />,
      data: rankings.lowestWinRate || [],
      sortKey: 'winRateAgainst',
      formatValue: (value) => `${value}%`
    }
  ];

  return (
    <div className="opponent-stats">
      <div className="stats-header">
        <h3 className="stats-title">对手分析</h3>
        <div className="stats-summary">
          共 {opponentList.length} 位对手
        </div>
      </div>

      {/* 排行榜区域 */}
      <div className="rankings-section">
        <Tabs 
          activeKey={activeRanking} 
          onChange={setActiveRanking}
          type="card"
          className="ranking-tabs"
        >
          {rankingTabs.map(tab => (
            <TabPane 
              tab={
                <span>
                  {tab.icon} {tab.label}
                </span>
              } 
              key={tab.key}
            >
              <RankingCard
                title={`${tab.label} Top 5`}
                icon={tab.icon}
                data={tab.data}
                sortKey={tab.sortKey}
                formatValue={tab.formatValue}
              />
            </TabPane>
          ))}
        </Tabs>
      </div>

      {/* 完整对手列表 */}
      <div className="opponent-list-section">
        <Card title="所有对手" className="opponent-list-card">
          <Table
            columns={columns}
            dataSource={opponentList.map((o, index) => ({ ...o, key: o.opponentId || index }))}
            pagination={{
              pageSize: 10,
              showSizeChanger: false,
              showTotal: (total) => `共 ${total} 位对手`
            }}
            scroll={{ x: 'max-content' }}
            size="small"
          />
        </Card>
      </div>
    </div>
  );
};

export default OpponentStats;













